import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../contexts/AuthContext'
import { usePermissions } from '../../contexts/PermissionContext'
import { useAlerts } from '../../contexts/AlertContext'
import { useWorkspace } from '../../contexts/WorkspaceContext'
import { messageAPI } from '../../services/api'
import apiService from '../../services/api'
import BlockedAction, { BlockedBanner } from '../common/BlockedAction'
import { useActionGate } from '../../contexts/BlockPolicyContext'
import { getSecurityLevelOptionsForRole } from '../../config/messageSecurity'
import {
  HiOutlinePaperAirplane,
  HiOutlineMicrophone,
  HiOutlineDocument,
} from 'react-icons/hi2'
import {
  Banner,
  Button,
  FormField,
  Input,
  Modal,
  Select,
  Textarea,
} from '../../components/ui'

const initialFormData = ({ preselectedCase, preselectedClient, preselectedSession } = {}) => ({
  type: 'text',
  title: '',
  content: '',
  security_level: 'green',
  message_scope: 'direct',
  case_id: preselectedCase || '',
  session_id: preselectedSession || '',
  client_id: preselectedClient || '',
  voice_file: null,
})

export default function MessageCreateModal({
  isOpen,
  onClose,
  onSuccess,
  preselectedCase,
  preselectedClient,
  preselectedSession,
}) {
  const { t } = useTranslation()
  const { isWorkspaceSwitched, ownerAccountId } = useAuth()
  const { userRole } = usePermissions()
  const { showSuccess, showError } = useAlerts()
  const { getEntity } = useWorkspace()

  const [formData, setFormData] = useState(() => initialFormData({ preselectedCase, preselectedClient, preselectedSession }))
  const [loading, setLoading] = useState(false)
  const [cases, setCases] = useState([])
  const [sessions, setSessions] = useState([])
  const [clients, setClients] = useState([])

  const sendAction = formData.type === 'voice' ? 'upload_voice' : 'upload_text'
  const sendGate = useActionGate(sendAction)
  const securityLevelOptions = getSecurityLevelOptionsForRole(userRole)
  const canBroadcast = userRole === 'owner' && !isWorkspaceSwitched

  useEffect(() => {
    if (!isOpen) return
    let cancelled = false
    ;(async () => {
      const [casesRes, clientsRes, sessionsRes] = await Promise.all([
        apiService.get('/cases/').catch(() => null),
        apiService.get('/clients/').catch(() => null),
        apiService.get('/sessions/').catch(() => null),
      ])
      if (cancelled) return
      const extract = (res) => {
        if (!res?.success) return []
        const d = res.data.data || res.data
        return d.data || d.results || []
      }
      setCases(extract(casesRes))
      setClients(extract(clientsRes))
      setSessions(extract(sessionsRes))
    })()
    return () => { cancelled = true }
  }, [isOpen, isWorkspaceSwitched, ownerAccountId])

  const setField = (name, value) => setFormData(prev => ({ ...prev, [name]: value }))

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (sendGate && sendGate.allowed === false) {
      // TODO: needs i18n key
      showError(sendGate.message || 'This action is currently blocked.')
      return
    }
    if (formData.type === 'text' && !formData.content.trim()) {
      showError(t('messages.create.validationContentRequired'))
      return
    }
    if (formData.type === 'voice' && !formData.voice_file) {
      showError(t('messages.create.validationVoiceRequired'))
      return
    }
    if (formData.message_scope === 'direct' && !formData.case_id && !formData.client_id && !formData.session_id) {
      showError(t('messages.create.validationTargetRequired', {
        caseEntity: getEntity('case').singular.toLowerCase(),
        clientEntity: getEntity('client').singular.toLowerCase(),
        sessionEntity: getEntity('session').singular.toLowerCase(),
      }))
      return
    }
    setLoading(true)
    try {
      const submit = new FormData()
      submit.append('type', formData.type)
      if (formData.title.trim()) submit.append('title', formData.title.trim())
      submit.append('security_level', formData.security_level)
      submit.append('message_scope', formData.message_scope)
      if (formData.type === 'text') {
        submit.append('content', formData.content)
      } else if (formData.type === 'voice' && formData.voice_file) {
        submit.append('voice_file', formData.voice_file)
      }
      if (formData.message_scope === 'direct') {
        if (formData.case_id) submit.append('case_id', formData.case_id)
        if (formData.client_id) submit.append('client_id', formData.client_id)
        if (formData.session_id) submit.append('session_id', formData.session_id)
      }
      const response = await messageAPI.createMessage(submit)
      if (response.success) {
        showSuccess(t('messages.create.sendSuccess'))
        onSuccess?.()
        onClose()
        setFormData(initialFormData({ preselectedCase, preselectedClient, preselectedSession }))
      } else {
        showError(response.error?.message || t('messages.create.sendFailed'))
      }
    } catch {
      showError(t('messages.create.sendFailed'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('messages.create.title')} size="lg">
      <form onSubmit={handleSubmit}>
        <Modal.Body className="space-y-5">
          <BlockedBanner action={sendAction} />

          {/* Message type — text vs voice */}
          <FormField label={t('messages.create.messageType')}>
            <div className="grid grid-cols-2 gap-3">
              <TypeTile
                icon={HiOutlineDocument}
                label={t('messages.create.text')}
                selected={formData.type === 'text'}
                onClick={() => setField('type', 'text')}
              />
              <TypeTile
                icon={HiOutlineMicrophone}
                label={t('messages.create.voice')}
                selected={formData.type === 'voice'}
                onClick={() => setField('type', 'voice')}
              />
            </div>
          </FormField>

          <FormField label={t('messages.create.titleLabel')}>
            <Input
              value={formData.title}
              onChange={(e) => setField('title', e.target.value)}
              placeholder={t('messages.create.titlePlaceholder')}
              maxLength={255}
            />
          </FormField>

          {formData.type === 'text' ? (
            <FormField label={t('messages.create.contentLabel')}>
              <Textarea
                value={formData.content}
                onChange={(e) => setField('content', e.target.value)}
                rows={4}
                placeholder={t('messages.create.contentPlaceholder')}
              />
            </FormField>
          ) : (
            <FormField label={t('messages.create.voiceFileLabel')}>
              <input
                type="file"
                accept="audio/*"
                onChange={(e) => setField('voice_file', e.target.files[0])}
                className="w-full text-sm text-ink-secondary file:me-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-brand-soft file:text-brand hover:file:bg-brand-soft/80"
              />
            </FormField>
          )}

          <FormField label={t('messages.create.securityLevel')}>
            <Select
              value={formData.security_level}
              onChange={(e) => setField('security_level', e.target.value)}
            >
              {securityLevelOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.icon} {option.label}
                </option>
              ))}
            </Select>
          </FormField>

          {canBroadcast && (
            <FormField label={t('messages.create.sendTo')}>
              <div className="grid grid-cols-2 gap-3">
                <ScopeTile
                  emoji="📧"
                  label={t('messages.create.directLabel')}
                  hint={t('messages.create.directHint')}
                  selected={formData.message_scope === 'direct'}
                  selectedTone="brand"
                  onClick={() => setField('message_scope', 'direct')}
                />
                <ScopeTile
                  emoji="📢"
                  label={t('messages.create.broadcastLabel')}
                  hint={t('messages.create.broadcastHint')}
                  selected={formData.message_scope === 'broadcast'}
                  selectedTone="warning"
                  onClick={() => setField('message_scope', 'broadcast')}
                />
              </div>
            </FormField>
          )}

          {formData.message_scope === 'direct' && (
            <>
              <FormField label={t('messages.create.caseOptional', { entity: getEntity('case').singular })}>
                <Select
                  value={formData.case_id}
                  onChange={(e) => setField('case_id', e.target.value)}
                >
                  <option value="">{t('messages.create.selectCase', { entity: getEntity('case').singular.toLowerCase() })}</option>
                  {cases.map((c) => (
                    <option key={c.id} value={c.id}>{c.title}</option>
                  ))}
                </Select>
              </FormField>
              <FormField label={t('messages.create.clientOptional', { entity: getEntity('client').singular })}>
                <Select
                  value={formData.client_id}
                  onChange={(e) => setField('client_id', e.target.value)}
                >
                  <option value="">{t('messages.create.selectClient', { entity: getEntity('client').singular.toLowerCase() })}</option>
                  {clients.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </Select>
              </FormField>
              <FormField label={t('messages.create.sessionOptional', { entity: getEntity('session').singular })}>
                <Select
                  value={formData.session_id}
                  onChange={(e) => setField('session_id', e.target.value)}
                >
                  <option value="">{t('messages.create.selectSession', { entity: getEntity('session').singular.toLowerCase() })}</option>
                  {sessions.map((s) => (
                    <option key={s.id} value={s.id}>{s.title}</option>
                  ))}
                </Select>
              </FormField>
            </>
          )}

          {formData.message_scope === 'broadcast' && (
            <Banner tone="warning" title={t('messages.create.broadcastTitle')}>
              <span dangerouslySetInnerHTML={{ __html: t('messages.create.broadcastWarning') }} />
            </Banner>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button type="button" variant="ghost" onClick={onClose} disabled={loading}>
            {t('common.cancel')}
          </Button>
          <BlockedAction action={sendAction} mode="inline">
            <Button
              type="submit"
              leadingIcon={HiOutlinePaperAirplane}
              loading={loading}
            >
              {t('messages.create.submit')}
            </Button>
          </BlockedAction>
        </Modal.Footer>
      </form>
    </Modal>
  )
}

function TypeTile({ icon: Icon, label, selected, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`p-4 rounded-xl border-2 transition-all text-center ${
        selected
          ? 'border-brand bg-brand-soft text-brand'
          : 'border-border bg-surface text-ink-secondary hover:border-border-strong'
      }`}
    >
      <Icon className="w-6 h-6 mx-auto mb-2" />
      <span className="block text-sm font-medium">{label}</span>
    </button>
  )
}

function ScopeTile({ emoji, label, hint, selected, selectedTone, onClick }) {
  const toneClasses = selectedTone === 'warning'
    ? 'border-warning bg-warning-soft text-warning'
    : 'border-brand bg-brand-soft text-brand'
  return (
    <button
      type="button"
      onClick={onClick}
      className={`p-3 rounded-xl border-2 transition-all text-center ${
        selected ? toneClasses : 'border-border bg-surface text-ink-secondary hover:border-border-strong'
      }`}
    >
      <div className="text-2xl mb-1" aria-hidden="true">{emoji}</div>
      <div className="font-medium text-sm">{label}</div>
      <div className="text-xs opacity-75 mt-0.5">{hint}</div>
    </button>
  )
}
